import { ApiServiceContainer } from "~/services/ApiServiceContainer";
import {FetchError} from "ofetch";
import type { Form, FormError, FormSubmitEvent } from "#ui/types";

import ApiError from "~/plugins/errors/apiError";
const VALIDATION_ERROR_CODE = 422;
const SERVER_ERROR_CODE = 500;

export function useApi(): ApiServiceContainer {
    const { $api } = useNuxtApp();

    return $api;
}

function mapLaravelErrorsIntoFormErrors(
    errors: Record<string, string[]>,
): FormError[] {
    return Object.entries(errors).map(([key, messages]) => ({
        path: key,
        message: messages[0] ?? "",
    }));
}

export function useApiError(error: unknown) {
    const isFetchError =
        error instanceof FetchError || error instanceof ApiError;

    const isValidationError =
        isFetchError && error.response?.status === VALIDATION_ERROR_CODE;
    const code = isFetchError ? error.response?.status : SERVER_ERROR_CODE;

    const bag = isValidationError
        ? mapLaravelErrorsIntoFormErrors(error.response?._data.errors)
        : [];

    return {
        isValidationError,
        code,
        bag,
    };
}

export interface FormSubmissionOptions {
    onSuccess?: (data: any) => void;
    onError?: (error: {
        isValidationError: boolean;
        code: number | undefined;
        bag: FormError[];
    }) => void;
    onDone?: () => void;
}

export function useFormSubmission<T = any, TR = T>(
    form: Ref<Form<T> | undefined>,
    callback: (event: FormSubmitEvent<T>) => Promise<TR>,
    options: Partial<{
        onSuccess?: (response: TR) => void;
        onError?: (error: {
            isValidationError: boolean;
            code: number | undefined;
            bag: FormError[];
        }) => void;
        onDone?: () => void;
    }> = {},
) {
    const submitting = ref<boolean>(false);
    async function onSubmit(event: FormSubmitEvent<T>) {
        form.value?.clear();

        try {
            submitting.value = true;
            const response = await callback(event);

            if (options.onSuccess) options.onSuccess(response);
        } catch (error) {
            const err = useApiError(error);

            if (err.isValidationError) {
                form.value?.setErrors(err.bag);
            }

            if (options.onError) options.onError(err);
        } finally {
            submitting.value = false;
            if (options.onDone) options.onDone();
        }
    }

    return {
        onSubmit,
        submitting,
    };
}
